import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "variable-picker" }
const _hoisted_2 = {
  key: 0,
  class: "variable-picker__empty"
}
const _hoisted_3 = { class: "variable-picker__group-label" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = {
  key: 2,
  class: "variable-picker__empty"
}

import { computed, onBeforeUnmount, ref } from "vue";
import { computePosition, autoUpdate, offset, flip, shift, size } from "@floating-ui/dom";
import Button from "@/components/ui/Button.vue";
import { useTemplateVariables } from "@/composables/templates";


export default /*@__PURE__*/_defineComponent({
  __name: 'TemplateVariablePicker',
  emits: ["insert"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const { data, isLoading } = useTemplateVariables();

const groups = computed(() => [
  {
    key: "contact",
    label: "Contact",
    fields: data.value?.contact ?? [],
    token: (key: string) => `{{${key}}}`,
  },
  {
    key: "company",
    label: "Company",
    fields: data.value?.company ?? [],
    token: (key: string) => `{{company.${key}}}`,
  },
  {
    key: "sender",
    label: "You (sender)",
    fields: data.value?.sender ?? [],
    token: (key: string) => `{{sender.${key}}}`,
  },
]);

const visibleGroups = computed(() => groups.value.filter((group) => group.fields.length > 0));
const hasAny = computed(() => visibleGroups.value.length > 0);

const isOpen = ref(false);
const triggerRef = ref<InstanceType<typeof Button> | null>(null);
const menuRef = ref<HTMLElement | null>(null);
let cleanupFloating: (() => void) | null = null;

const positionMenu = () => {
  const referenceEl = triggerRef.value?.$el as HTMLElement | undefined;
  if (!referenceEl || !menuRef.value) return;

  const update = () => {
    if (!referenceEl || !menuRef.value) return;
    computePosition(referenceEl, menuRef.value, {
      placement: "bottom-end",
      strategy: "fixed",
      middleware: [
        offset({ mainAxis: 6, crossAxis: 28 }),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
        size({
          padding: 8,
          apply({ availableHeight, elements }) {
            Object.assign(elements.floating.style, {
              maxHeight: `${Math.max(160, availableHeight)}px`,
            });
          },
        }),
      ],
    }).then(({ x, y }) => {
      if (!menuRef.value) return;
      Object.assign(menuRef.value.style, {
        position: "fixed",
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  };

  cleanupFloating = autoUpdate(referenceEl, menuRef.value, update, {
    ancestorScroll: true,
    ancestorResize: true,
    elementResize: true,
  });
};

const teardownFloating = () => {
  if (cleanupFloating) {
    cleanupFloating();
    cleanupFloating = null;
  }
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  const triggerEl = triggerRef.value?.$el as HTMLElement | undefined;
  if (menuRef.value?.contains(target) || triggerEl?.contains(target)) return;
  close();
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") close();
};

const open = () => {
  isOpen.value = true;
  requestAnimationFrame(() => {
    positionMenu();
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeydown);
  });
};

const close = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  teardownFloating();
  document.removeEventListener("mousedown", handleClickOutside);
  document.removeEventListener("keydown", handleKeydown);
};

const toggle = () => (isOpen.value ? close() : open());

const select = (token: string) => {
  emit("insert", token);
  close();
};

onBeforeUnmount(() => {
  teardownFloating();
  document.removeEventListener("mousedown", handleClickOutside);
  document.removeEventListener("keydown", handleKeydown);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Button, {
      ref_key: "triggerRef",
      ref: triggerRef,
      type: "button",
      variant: "secondary",
      size: "sm",
      icon: "mdi:code-braces",
      onClick: toggle
    }, {
      default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
        _createTextVNode(" Insert variable ", -1)
      ]))]),
      _: 1
    }, 512),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "menuRef",
            ref: menuRef,
            class: "variable-picker__menu",
            role: "menu",
            "aria-label": "Insert variable"
          }, [
            (_unref(isLoading))
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, "Loading variables…"))
              : (hasAny.value)
                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(visibleGroups.value, (group) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: group.key,
                      class: "variable-picker__group"
                    }, [
                      _createElementVNode("div", _hoisted_3, _toDisplayString(group.label), 1),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.fields, (field) => {
                        return (_openBlock(), _createElementBlock("button", {
                          key: field.key,
                          type: "button",
                          class: "variable-picker__item",
                          onClick: ($event: any) => (select(group.token(field.key)))
                        }, _toDisplayString(field.label), 9, _hoisted_4))
                      }), 128))
                    ]))
                  }), 128))
                : (_openBlock(), _createElementBlock("div", _hoisted_5, "No variables available"))
          ], 512))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})